import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { StatusAnnouncerService } from 'src/app/shared/services/status-announcer.service';
import { AppUpdateGate } from '../../offline/app-update-gate';
import { ChunkErrorRecoveryService } from '../../services/chunk-error-recovery.service';

@Component({
  selector: 'app-update-banner',
  templateUrl: './app-update-banner.component.html',
  styleUrls: ['./app-update-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppUpdateBannerComponent implements OnDestroy {
  /**
   * WCAG 4.1.3. The banner is the one notice that tells a user why the app is about to reload under
   * them, so its appearance is announced -- through the root announcer, once, as it goes up. Not from
   * a region in this template: the banner is `*ngIf`-ed into existence (a live role on it arrives in
   * the same frame as its text and is commonly read by nothing), and a permanent hidden copy kept
   * the sentence in the page for the virtual cursor to meet a second time beside the banner for as
   * long as it stayed up (MR !197 review, round 11).
   */
  readonly needsReload$ = this.recovery.needsReload$.pipe(
    distinctUntilChanged(),
    tap((needsReload) => {
      if (needsReload) {
        this.announcer.announceKey('common.appUpdate.bannerAnnounce');
      } else {
        this.heldBack = false;
      }
    }),
  );

  /**
   * The last click was held back: the offline downloads of Scan are running and a reload would cut
   * them short (`AppUpdateGate`). Said beside the button and announced, instead of a click that did
   * nothing at all (review round 4, 26/10/2026). Gone as soon as the gate is released: the sentence
   * says "once they finish", and it stayed after they had (review round 5, 26/10/2026).
   */
  heldBack = false;

  /** "Più tardi" is offered: a new build is the only reason the banner shows. */
  readonly canDismiss$ = this.recovery.canDismiss$;

  private readonly subs = new Subscription();
  /** Where the focus was before it entered the banner: "Più tardi" gives it back there. */
  private focusBefore: HTMLElement | null = null;

  constructor(
    private readonly recovery: ChunkErrorRecoveryService,
    private readonly announcer: StatusAnnouncerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
    gate: AppUpdateGate,
  ) {
    this.subs.add(
      gate.held$.pipe(filter((held) => !held && this.heldBack)).subscribe(() => {
        this.heldBack = false;
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    const from = event.relatedTarget;
    if (from instanceof HTMLElement && !this.host.nativeElement.contains(from)) {
      this.focusBefore = from;
    }
  }

  /**
   * Put the new build off: the banner goes until the next build (or a chunk error). The button
   * that has the focus goes with it: the focus goes back where it came from, or to the page's
   * `<main>`, instead of falling on `<body>` (WCAG 2.4.3, review round 9).
   */
  later(): void {
    const hadFocus = this.host.nativeElement.contains(document.activeElement);
    const back = this.focusBefore;
    this.focusBefore = null;
    this.heldBack = false;
    this.recovery.dismissNewBuild();
    if (!hadFocus) {
      return;
    }
    const target =
      back?.isConnected && !this.host.nativeElement.contains(back)
        ? back
        : document.getElementById('main-content');
    target?.focus();
  }

  reload(): void {
    this.heldBack = !this.recovery.reloadNow();
    if (this.heldBack) {
      this.announcer.announceKey('common.appUpdate.heldBack');
    }
    this.cdr.markForCheck();
  }
}
